import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "login-screen" }
const _hoisted_2 = { class: "login-card" }
const _hoisted_3 = { class: "mt-2" }

import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import AppTextField from "@/shared/components/ui/AppTextField.vue";
import AppAuthBrand from "@/modules/auth/components/AppAuthBrand.vue";
import { getContextKind, resetPassword } from "@/modules/auth/services/auth.api";
import { useSnackbarStore } from "@/shared/stores/snackbar";
import { useAppStore } from '@/shared/stores/app';
import fallbackLogo from '@/assets/s-logo.png';
import AppButtonFlat from "@/shared/components/ui/AppButtonFlat.vue";
import { getActiveContext, getAuthToken, getCustomer, setCustomer } from "@/modules/auth/services/auth.storage";


export default /*@__PURE__*/_defineComponent({
  __name: 'ChangePassword',
  setup(__props) {

const email = ref("");
const password = ref("");
const confirmPassword = ref("");
const showPassword = ref(false);
const showConfirmPassword = ref(false);
const isResettingPassword = ref(false);
const router = useRouter();
const route = useRoute();
const emailError = ref("");
const passwordError = ref("");
const confirmPasswordError = ref("");
const snackbar = useSnackbarStore();
const appStore = useAppStore();
const companyName = computed(() =>
  (appStore.company?.name || appStore.appName || "GlobalApp").toString().trim(),
);
const logo = computed(() => appStore.company?.logoUrl || fallbackLogo);
const subtitle = computed(() => "Set a new password to secure your account.");
const brandData = computed(() => ({
  logo: logo.value,
  title: companyName.value,
  subtitle: subtitle.value,
}));

onMounted(() => {
  // Prefer email from stored auth_customer (logged-in flow).
  try {
    const customer: any = getCustomer<any>() || null;
    const storedEmail = typeof customer?.email === "string" ? customer.email.trim() : "";
    if (storedEmail && !email.value) {
      email.value = storedEmail;
    } else if (!storedEmail) {
      snackbar.error("Missing account email. Please log in again.");
      router.replace({ name: "login.page" }).catch(() => {});
      return;
    }
  } catch {
    snackbar.error("Unable to read account data. Please log in again.");
    router.replace({ name: "login.page" }).catch(() => {});
  }
});


async function handleChangePassword() {
  emailError.value = "";
  passwordError.value = "";
  confirmPasswordError.value = "";


  if (!password.value) {
    passwordError.value = "Password is required";
  } else if (password.value.length < 6) {
    passwordError.value = "Minimum 6 characters";
  }

  if (!confirmPassword.value) {
    confirmPasswordError.value = "Confirm your password";
  } else if (confirmPassword.value !== password.value) {
    confirmPasswordError.value = "Passwords do not match";
  }

  if (emailError.value || passwordError.value || confirmPasswordError.value) {
    return;
  }

  try {
    isResettingPassword.value = true;
    const response = await resetPassword({
      email: email.value,
      password: password.value,
      password_confirmation: confirmPassword.value,
    });

    snackbar.success(response?.message || "Password changed successfully.");

    // Mark first-login flow as completed locally so dashboard middleware doesn't bounce us back.
    try {
      const existing: any = getCustomer<any>() || null;
      if (existing && typeof existing === "object") {
        setCustomer({ ...existing, first_login: false });
        window.dispatchEvent(new Event("auth-changed"));
      }
    } catch {
      // ignore
    }

    // If already logged in, route back to the correct dashboard; otherwise fall back to login.
    try {
      const hasToken = Boolean(getAuthToken());
  
      if (hasToken) {
        const kind = getContextKind(getActiveContext() as any);
        if (kind === "customer") {
          router.replace({ name: "customer.dashboard" }).catch(() => {});
          return;
        }
        if (kind === "vendor") {
          router.replace({ name: "vendor.dashboard" }).catch(() => {});
          return;
        }

        router.replace({ name: "marketplace.home" }).catch(() => {});
        return;
      }
    } catch {
      // ignore
    }

    router.replace({ name: "login.page" });
  } catch (error) {
    const errors = error?.response?.data?.errors;
    const message =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      "Unable to reset password";

    if (errors) {
      if (errors.email?.length) emailError.value = errors.email[0];
      if (errors.password?.length) passwordError.value = errors.password[0];
      if (errors.password_confirmation?.length) {
        confirmPasswordError.value = errors.password_confirmation[0];
      }
    }

    if (!emailError.value && !passwordError.value && !confirmPasswordError.value) {
      snackbar.error(message);
    }
  } finally {
    isResettingPassword.value = false;
  }
}

return (_ctx: any,_cache: any) => {
  const _component_v_form = _resolveComponent("v-form")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createVNode(AppAuthBrand, {
        data: brandData.value,
        logoAlt: companyName.value,
        "subtitle-size": "16px",
        "title-color": "#151a24",
        "subtitle-color": "#64748b",
        gap: "10px"
      }, null, 8, ["data", "logoAlt"]),
      _createElementVNode("div", null, [
        _createVNode(_component_v_form, { class: "form-style" }, {
          default: _withCtx(() => [
            _createVNode(AppTextField, {
              modelValue: password.value,
              "onUpdate:modelValue": [
                _cache[0] || (_cache[0] = ($event: any) => ((password).value = $event)),
                _cache[1] || (_cache[1] = ($event: any) => (passwordError.value = ''))
              ],
              type: showPassword.value ? 'text' : 'password',
              autocomplete: "new-password",
              "prepend-inner-icon": "mdi-lock-outline",
              "append-inner-icon": showPassword.value ? 'mdi-eye-off-outline' : 'mdi-eye-outline'
              ,
              placeholder: "New password",
              class: "auth-input",
              "error-messages": passwordError.value,
              "onClick:appendInner": _cache[2] || (_cache[2] = ($event: any) => (showPassword.value = !showPassword.value))
            }, null, 8, ["modelValue", "type", "append-inner-icon", "error-messages"]),
            _createVNode(AppTextField, {
              modelValue: confirmPassword.value,
              "onUpdate:modelValue": [
                _cache[3] || (_cache[3] = ($event: any) => ((confirmPassword).value = $event)),
                _cache[4] || (_cache[4] = ($event: any) => (confirmPasswordError.value = ''))
              ],
              type: showConfirmPassword.value ? 'text' : 'password',
              autocomplete: "new-password",
              "prepend-inner-icon": "mdi-lock-check-outline",
              "append-inner-icon": showConfirmPassword.value ? 'mdi-eye-off-outline' : 'mdi-eye-outline'
              ,
              placeholder: "Confirm new password",
              class: "auth-input",
              "error-messages": confirmPasswordError.value,
              "onClick:appendInner": _cache[5] || (_cache[5] = ($event: any) => (showConfirmPassword.value = !showConfirmPassword.value))
            }, null, 8, ["modelValue", "type", "append-inner-icon", "error-messages"])
          ]),
          _: 1
        }),
        _createElementVNode("div", _hoisted_3, [
          _createVNode(AppButtonFlat, {
            block: "",
            onClick: handleChangePassword,
            loading: isResettingPassword.value,
            disabled: isResettingPassword.value
          }, {
            default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
              _createTextVNode(" Change Password ", -1)
            ]))]),
            _: 1
          }, 8, ["loading", "disabled"])
        ])
      ])
    ])
  ]))
}
}

})